import React, { useContext } from "react";
import { motion } from "framer-motion";
import StyleContext from "../../contexts/StyleContext";
import { workExperiences } from "../../portfolio";

export default function WorkExperienceModern() {
  const { isDark } = useContext(StyleContext);

  if (!workExperiences.viewExperiences) {
    return null;
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <div className="relative py-20 md:py-32 overflow-hidden" id="experience">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-blue-900/20 dark:to-indigo-900/20" />

      {/* Floating Orb */}
      <motion.div
        className="absolute -left-20 top-1/3 w-96 h-96 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-full opacity-20 blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          x: [0, 50, 0],
          y: [0, -30, 0],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <div className="relative z-10 container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            className="inline-block mb-4"
            whileHover={{ scale: 1.05 }}
          >
            <span className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full text-sm font-semibold shadow-lg">
              Career Journey
            </span>
          </motion.div>
          <h2 className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 ${
            isDark ? "text-white" : "text-gray-900"
          }`}>
            Work Experience
          </h2>
          <p className={`text-lg md:text-xl max-w-2xl mx-auto ${
            isDark ? "text-gray-400" : "text-gray-600"
          }`}>
            My professional journey through various roles and companies
          </p>
        </motion.div>

        {/* Timeline */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="max-w-5xl mx-auto relative"
        >
          {/* Timeline Line */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 via-indigo-500 to-purple-500 transform md:-translate-x-1/2" />

          {/* Experience Cards */}
          <div className="space-y-12">
            {workExperiences.experience.map((exp, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className={`relative flex flex-col md:flex-row items-start gap-8 ${
                  index % 2 === 0 ? "md:flex-row-reverse" : ""
                }`}
              >
                {/* Timeline Dot */}
                <div className="absolute left-8 md:left-1/2 transform -translate-x-1/2 z-10">
                  <motion.div
                    whileHover={{ scale: 1.3 }}
                    className="w-4 h-4 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 border-4 border-white dark:border-gray-900 shadow-lg"
                  />
                </div>

                {/* Content Card */}
                <div className={`w-full md:w-auto flex-1 pl-16 md:pl-0 ${
                  index % 2 === 0 ? "md:pr-12" : "md:pl-12"
                }`}>
                  <motion.div
                    whileHover={{
                      scale: 1.02,
                      transition: { duration: 0.2 }
                    }}
                    className="glass backdrop-blur-xl rounded-2xl p-6 md:p-8 bg-white/60 dark:bg-gray-800/60 border border-white/20 dark:border-gray-700/20 shadow-xl hover:shadow-2xl hover:shadow-blue-500/20 transition-all duration-300"
                  >
                    {/* Company Logo & Date */}
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-white dark:bg-gray-700 p-2 shadow-lg flex items-center justify-center">
                        <img
                          src={exp.companylogo.default || exp.companylogo}
                          alt={exp.company}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div>
                        <h3 className={`text-xl md:text-2xl font-bold ${
                          isDark ? "text-white" : "text-gray-900"
                        }`}>
                          {exp.role}
                        </h3>
                        <p className="text-sm font-medium gradient-text">
                          {exp.company}
                        </p>
                      </div>
                    </div>

                    {/* Date Badge */}
                    <div className="inline-block mb-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        isDark
                          ? "bg-blue-500/20 text-blue-300"
                          : "bg-blue-100 text-blue-700"
                      }`}>
                        📅 {exp.date}
                      </span>
                    </div>

                    {/* Description */}
                    {/* <p className={`mb-4 leading-relaxed ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}>
                      {exp.desc}
                    </p> */}

                    {/* Bullets */}
                    {/* {exp.descBullets && exp.descBullets.length > 0 && (
                      <div className="space-y-3">
                        {exp.descBullets.map((bullet, bulletIndex) => (
                          <motion.div
                            key={bulletIndex}
                            initial={{ opacity: 0, x: -10 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: bulletIndex * 0.1 }}
                            className="flex items-start gap-3"
                          >
                            <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 mt-2" />
                            <p className={`text-sm leading-relaxed ${
                              isDark ? "text-gray-400" : "text-gray-600"
                            }`}>
                              {bullet}
                            </p>
                          </motion.div>
                        ))}
                      </div>
                    )} */}
                  </motion.div>
                </div>

                {/* Spacer for alternating layout */}
                <div className="hidden md:block flex-1" />
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Bottom Decorative Element */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="mt-20 flex justify-center"
        >
          <div className="flex items-center gap-2">
            <div className="h-px w-20 bg-gradient-to-r from-transparent via-blue-500 to-transparent" />
            <div className="w-2 h-2 rounded-full bg-blue-500" />
            <div className="h-px w-20 bg-gradient-to-r from-transparent via-indigo-500 to-transparent" />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
